import { useCallback, useEffect, useRef, useState } from 'react';
import { getMailLists } from 'api/canmail/CANmailAPI';
import { GetLotusBlossomSchedule, ScheduledSession } from 'api/lotusBlossom/lotusBlossomAPI';
import {
	findCurrentSessions,
	findParticipantsListId,
	findSubscribersListId,
	findUpcomingSessions,
	parseThemeLists,
	ThemeSession,
	withEndDates,
} from '../libs/themeLists';

export type SessionLoadState = 'loading' | 'error' | 'ready';

export interface QuantumHealingSession {
	state: SessionLoadState;
	/** Sessions running today — started and not yet past their last day. */
	current: ThemeSession[];
	/** Sessions yet to start, soonest first. */
	upcoming: ThemeSession[];
	/** Every dated session, earliest first, each with its last day where known. */
	sessions: ThemeSession[];
	/**
	 * False when the schedule couldn't be read, so every session is being
	 * treated as lasting a single day.
	 */
	endDatesKnown: boolean;
	/** list_id of "Quantum Healing Subscribers", or null if the list doesn't exist. */
	subscribersListId: number | null;
	/** list_id of "Quantum Healing Participants", or null if the list doesn't exist. */
	participantsListId: number | null;
	reload: () => void;
}

/**
 * Resolves the sessions and the list ids the tabs need from one getMailLists
 * call, shared by the header, the rosters and the client directory. The
 * schedule is read alongside it for each session's last day, which the list
 * names don't carry.
 */
export function useQuantumHealingSession(): QuantumHealingSession {
	const [state, setState] = useState<SessionLoadState>('loading');
	const [current, setCurrent] = useState<ThemeSession[]>([]);
	const [upcoming, setUpcoming] = useState<ThemeSession[]>([]);
	const [sessions, setSessions] = useState<ThemeSession[]>([]);
	const [endDatesKnown, setEndDatesKnown] = useState(true);
	const [subscribersListId, setSubscribersListId] = useState<number | null>(null);
	const [participantsListId, setParticipantsListId] = useState<number | null>(null);
	// A reload (after Sync) can start while the first load is still running;
	// only the latest may write, or the older list could land last and hide
	// the sessions the sync just created.
	const requestRef = useRef(0);

	const load = useCallback(async () => {
		const request = ++requestRef.current;
		const isCurrent = () => request === requestRef.current;

		setState('loading');
		try {
			// Without the schedule the page still works, just less precisely: each
			// session is treated as one day, so a running one moves to Past early.
			const loadSchedule = () =>
				GetLotusBlossomSchedule().catch((error): ScheduledSession[] | null => {
					console.error('[QuantumHealing] schedule unavailable:', error);
					return null;
				});

			const [lists, scheduled] = await Promise.all([getMailLists(), loadSchedule()]);
			if (!isCurrent()) return;

			// getMailLists swallows its errors and resolves undefined, so a
			// non-array result is a failed request, not an empty instance —
			// reporting "no session" would hide an outage behind an empty state.
			if (!Array.isArray(lists)) {
				throw new Error('getMailLists returned a non-array result');
			}

			const parsed = withEndDates(parseThemeLists(lists), scheduled ?? []);
			setSessions(parsed);
			setCurrent(findCurrentSessions(parsed));
			setUpcoming(findUpcomingSessions(parsed));
			setEndDatesKnown(scheduled !== null);
			setSubscribersListId(findSubscribersListId(lists));
			setParticipantsListId(findParticipantsListId(lists));
			setState('ready');
		} catch (error) {
			if (!isCurrent()) return;
			console.error('[QuantumHealing] useQuantumHealingSession load:', error);
			setState('error');
		}
	}, []);

	useEffect(() => {
		load();
		return () => {
			requestRef.current += 1;
		};
	}, [load]);

	return {
		state,
		current,
		upcoming,
		sessions,
		endDatesKnown,
		subscribersListId,
		participantsListId,
		reload: load,
	};
}

export default useQuantumHealingSession;
